import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import {
  hasPagePermissionWithAdmin,
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
} from "../../components/Permission/PermissionService";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import EmployeeForm, { defaultEmployeeForm } from "../../components/EmployeeForm";
import { getGenders } from "../../components/Api/GmsGender";
import { getCountries } from "../../components/Api/GmsCountry";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getDepartments } from "../../components/Api/GmsDepartment";
import { getDesignations } from "../../components/Api/GmsDesignation";
import { getBranches } from "../../components/Api/GmsBranch";

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };

  let businessId, branchId;

  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }

  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
    companyId: businessId,
  };
};

export default function AddEmployeePage() {
  const navigate = useNavigate();

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsAddEmployee');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsAddEmployee');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsAddEmployee');

  const session = useMemo(() => getUserSession(), []);

  const [form, setForm] = useState(() => ({
    ...defaultEmployeeForm,
    employee_CreatedDate: new Date(),
    employee_CreatedBy: Number(localStorage.getItem("userId")) || 1,
    employee_ModifiedDate: new Date(),
    employee_ModifiedBy: Number(localStorage.getItem("userId")) || 1
  }));
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [genders, setGenders] = useState([]);
  const [countries, setCountries] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [branches, setBranches] = useState([]);

  // Load branches based on selected business
  const loadBranchesForBusiness = async (businessId) => {
    if (!businessId) {
      setBranches([]);
      // Clear branch selection when business is cleared
      setForm(prev => ({ ...prev, employee_Branch_Id: null }));
      return;
    }
    try {
      // Use Branch_Buisness_Id as the filter parameter (matches backend filter property)
      const response = await getBranches({ Branch_Buisness_Id: businessId, branch_Active: true });
      // Handle both array and object responses
      const branchesData = Array.isArray(response) ? response : (response?.data || response || []);
      // Additional client-side filter to ensure only branches for selected business are shown
      const filteredBranches = branchesData.filter(branch => branch.branch_Buisness_Id === businessId);
      setBranches(filteredBranches);
      // Clear branch selection when business changes
      setForm(prev => ({ ...prev, employee_Branch_Id: null }));
    } catch (error) {
      console.error("Error loading branches:", error);
      setBranches([]);
    }
  };

  useEffect(() => {
    if (hasPermission) {
      const fetchData = async () => {
        try {
          const [
            gendersData,
            countriesData,
            businessesData,
            departmentsData,
            designationsData
          ] = await Promise.all([
            getGenders(),
            getCountries(),
            getBusinesses({ business_Active: true }),
            getDepartments({}),
            getDesignations({})
          ]);

          setGenders(gendersData || []);
          setCountries(countriesData || []);
          setBusinesses(businessesData || []);
          setDepartments(departmentsData || []);
          setDesignations(designationsData || []);
          // Don't load all branches initially - wait for business selection
          setBranches([]);
        } catch (error) {
          console.error("Error fetching data:", error);
          setGenders([]);
          setCountries([]);
          setBusinesses([]);
          setDepartments([]);
          setDesignations([]);
          setBranches([]);
        }
      };
      fetchData();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  // Auto-populate company if user has companyId
  useEffect(() => {
    if (session.businessId && businesses.length > 0 && !form.employee_Business_Id) {
      const business = businesses.find(b => b.business_Id === session.businessId);
      if (business) {
        setForm(prev => ({
          ...prev,
          employee_Business_Id: session.businessId
        }));
      }
    }
  }, [businesses, session.businessId]);

  // Auto-populate branch if user has branchId
  useEffect(() => {
    if (session.branchId && branches.length > 0 && form.employee_Business_Id && !form.employee_Branch_Id) {
      const branch = branches.find(b => b.branch_Id === session.branchId);
      if (branch) {
        setForm(prev => ({
          ...prev,
          employee_Branch_Id: session.branchId
        }));
      }
    }
  }, [branches, session.branchId, form.employee_Business_Id]);

  // Load branches when business is selected
  useEffect(() => {
    if (form.employee_Business_Id) {
      loadBranchesForBusiness(form.employee_Business_Id);
    } else {
      setBranches([]);
    }
  }, [form.employee_Business_Id]);

  const handleSuccess = () => {
    // Reset form after successful save, but preserve business and branch IDs
    // so they remain populated and disabled based on session
    setForm({
      ...defaultEmployeeForm,
      employee_CreatedDate: new Date(),
      employee_CreatedBy: Number(localStorage.getItem("userId")) || 1,
      employee_ModifiedDate: new Date(),
      employee_ModifiedBy: Number(localStorage.getItem("userId")) || 1,
      // Preserve business and branch IDs from session
      employee_Business_Id: session.businessId || null,
      employee_Branch_Id: session.branchId || null
    });
    setErrors({});

    // Optionally navigate to employee list
    // navigate('/GmsEmployee');
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid={true}>
        <Breadcrumbs title="Add Employee" breadcrumbItem="Employee Management" />

        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <h4 className="card-title mb-4">Add New Employee</h4>
                <EmployeeForm
                  form={form}
                  setForm={setForm}
                  errors={errors}
                  setErrors={setErrors}
                  loading={loading}
                  setLoading={setLoading}
                  isEditing={false}
                  onSuccess={handleSuccess}
                  genders={genders}
                  countries={countries}
                  businesses={businesses}
                  departments={departments}
                  designations={designations}
                  branches={branches}
                  hasDeletePermission={hasDeletePermission}
                  isCompanyDisabled={!!session.businessId}
                  isBranchDisabled={!!session.branchId}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
